import { useTranslation } from 'next-i18next'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { P, match } from 'ts-pattern'

import Button from '@nxt-insurance/ui-kit/components/Button'
import { SCREEN_SIZE } from '@nxt-insurance/ui-kit/constants'

import type { CtaItem, PageType } from '@/cms/pages'
import { BUTTONS_IDS, EVENTS } from '@/constants/analytics'
import { useAnalytics } from '@/context/AnalyticsContext'
import { useUrlFromWebUrlReference } from '@/hooks/useUrlFromWebUrlReference'
import { useWindowWidth } from '@/hooks/useWindowWidth'
import { getProductButtonTarget, removeImpermanentSearchParams } from '@/utils/url'

export const HeaderCtaButton = (props: { cta: CtaItem; pageType?: PageType }) => {
  const { track } = useAnalytics()
  const { t } = useTranslation()
  const { query, locale } = useRouter()
  const whitelistedSearchParams = removeImpermanentSearchParams(query).toString()
  const defaultButtonLabel = t('header.nav.cta')
  const windowWidth = useWindowWidth()

  const productPagePattern = {
    _type: P.union(
      'website.pageType.productPage',
      'website.pageType.bundleProductPage',
      'website.pageType.mgaLightProductPage'
    ),
  }

  const usedLabel = match(props.pageType)
    .with(productPagePattern, () => props.cta.productLabel)
    .otherwise(() => props.cta.label ?? defaultButtonLabel)

  const hrefFromDefaultCta = useUrlFromWebUrlReference(props.cta.target ?? undefined, {
    optional: true,
  })

  const isDesktop = windowWidth >= SCREEN_SIZE.XL
  const buttonTarget = getProductButtonTarget(props.pageType, locale)

  const searchParams = whitelistedSearchParams ? `?${whitelistedSearchParams}` : ''
  const defaultButtonTarget = `${searchParams}#products`

  const buttonHref = buttonTarget ? buttonTarget : hrefFromDefaultCta

  return (
    <Link href={buttonHref ?? defaultButtonTarget}>
      <Button
        size={isDesktop ? 'slim' : undefined}
        label={usedLabel}
        type="default"
        onPress={() => {
          track(EVENTS.ButtonClicked, {
            buttonId: BUTTONS_IDS.HEADER_CTA,
            type: 'link',
            href: buttonHref,
            t,
          })
        }}
      />
    </Link>
  )
}
